import type { AttackDefinition } from './combat-types.js';

type Weapon = Pick<AttackDefinition, 'id' | 'name' | 'mode' | 'light' | 'twoHanded'>;

/** A weapon that can be held in the off hand at all: a one-handed melee weapon. */
export const offHandable = (weapon: Weapon) => weapon.mode === 'melee' && !weapon.twoHanded;

/**
 * Why `off` cannot be carried in the off hand beside the main weapon `main`, or undefined when it can. Core Rules, Dual Wielding: two
 * Light weapons, any two one-handed weapons at STR 3 or more, or one that is not Light at STR 2. A one-handed ranged main weapon
 * (javelins) leaves the off hand free, but only two melee weapons are dual wielded.
 */
export function offHandRefusal(main: Weapon, off: Weapon, strength: number): string | undefined {
  if (main.twoHanded) return `The ${main.name} needs both hands.`;
  if (!offHandable(off)) return `The ${off.name} cannot be held in the off hand.`;
  if (main.mode !== 'melee') return undefined;
  const heavy = Number(!main.light) + Number(!off.light);
  if (heavy === 2 && strength < 3) return 'Dual wielding two weapons that are not Light needs STR 3.';
  if (heavy === 1 && strength < 2) return 'Dual wielding a weapon that is not Light needs STR 2.';
  return undefined;
}

/** Whether an attack with `main` is dual wielded with `off` in the other hand. */
export function dualWielded(main: Weapon, off: Weapon | undefined, strength: number): boolean {
  return !!off && main.mode === 'melee' && !offHandRefusal(main, off, strength);
}
